import React from "react";
import Stack from "@mui/material/Stack"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControlLabel,
    Divider
} from "@mui/material";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import SearchField from "./SearchField";
import TemporalFilters from "./TemporalFilters";
import GenericParameterSelect from "./GenericParameterSelect";
import WorkgroupsAutocomplete from "../../../../components/inputs/WorkgroupsAutocomplete";
import Elevation from "../../../../components/inputs/Elevation";
import AspectsAutocomplete from "../../../../components/inputs/AspectsAutocomplete";
import MicroZonesAutocomplete from "../../../../components/inputs/MicroZonesAutocomplete";
import StationTypeSelect from "../../../../components/inputs/StationTypeSelect";
import Model1StatusAutocomplete from "../../../../components/inputs/Model1StatusAutocomplete";
import Model1ValidationStatusAutocomplete from "../../../../components/inputs/Model1ValidationStatusAutocomplete";
import UsersAutocomplete from "../../../../components/inputs/UsersAutocomplete";
import StationWritePermissionCheckBox from "../../../../components/inputs/StationWritePermissionCheckBox";
import StationVisibilityAutocomplete from "../../../../components/inputs/StationVisibilityAutocomplete";
import ProvinceAutocomplete from "../../../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../../../components/inputs/MunicipalityAutocomplete";
import RegionAutocomplete from "../../../../components/inputs/RegionAutocomplete";
import SelectFromRegistry from "../../../../components/inputs/SelectFromRegistry";
import CurrentlyActiveSelect from "../../../../components/inputs/CurrentlyActiveSelect";
import AcceptsModelSelect from "../../../../components/inputs/AcceptsModelSelect";


const accordionStyle = {
    disableGutters: true,
    elevation: 0,
    square: true,
    sx: {
        backgroundColor: "transparent",
        "&:before": {display: "none"},
        '.MuiAccordionSummary-content': {
            margin: '8px 0px',
            '&.Mui-expanded': {
                margin: '8px 0px',
            },
        },
        '.MuiAccordionDetails-root': {
            padding: '12px',
            //backgroundColor: "#e3f2fd", // Light blue (MUI blue 50)
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: "4px",
        }
    }
};


const FiltersPanel = ({layersState, featuresState}) => {
    const {
        layers = {},
        toggleLayer,
        updateCommonFilter,
        updateLayerFilters,
        temporalState,
        setVisibleParam,
        selectedWorkgroups,
        setSelectedWorkgroups
    } = layersState;
    const {t} = useTranslation(["webgis", "stations", "incidents"]);

    const registry = featuresState?.webgisRegistryQuery?.data ?? {};

    const stationsLayer = layers.stations ?? {};
    const microzonesLayer = layers.microzones ?? {};
    const models23Layer = layers.models23 ?? {};
    const model1Stations = layers.model1Stations ?? {};
    const incidentsLayer = layers.incidents ?? {};

    const incidentRegistry = featuresState?.incidentRegistryQuery?.data ?? {};
    const incidentSettings = featuresState?.incidentSettingQuery?.data ?? {};

    const commonMicroZones = layers.commonFilters.microZones || [];

    return <Stack py={1} spacing={4}>
        <SearchField onChange={updateCommonFilter}/>
        <TemporalFilters temporalState={temporalState}
                         accordionStyle={accordionStyle}/>

        <Accordion {...accordionStyle}>
            <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                <Typography variant="body2">{t("filtersPanel.geoFilters.title", "Filtri geografici")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <WorkgroupsAutocomplete
                        selectedWorkgroups={selectedWorkgroups}
                        setSelectedWorkgroups={(value) => {
                            setSelectedWorkgroups(value);
                            updateCommonFilter("workgroupIds", value.map(w => w.id).join(','));
                        }}
                    />

                    <Stack direction="row" spacing={1}>
                        <Elevation
                            elevation={layers.commonFilters.elevationGte}
                            setElevation={(val) => updateCommonFilter("elevationGte", val)}
                            labelType="gte"
                            size="small"
                        />
                        <Elevation
                            elevation={layers.commonFilters.elevationLte}
                            setElevation={(val) => updateCommonFilter("elevationLte", val)}
                            labelType="lte"
                            size="small"
                        />
                    </Stack>

                    <AspectsAutocomplete
                        selectedAspects={layers.commonFilters.aspects}
                        setSelectedAspects={(val) => updateCommonFilter("aspects", val)}
                        size="small"
                    />

                    <MicroZonesAutocomplete
                        selectedMicroZones={commonMicroZones}
                        setSelectedMicroZones={(val) => updateCommonFilter("microZones", val)}
                        microZoneFilter={{workgroup_id__in: layers.commonFilters.workgroupIds}}
                        size="small"
                        fullWidth
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>

        <Stack spacing={1} mt={1}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                {t("filtersPanel.layersTitle")}
            </Typography>

            <Accordion {...accordionStyle} >
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                    <FormControlLabel
                        control={
                            <Checkbox checked={model1Stations.active} onChange={() => toggleLayer("model1Stations")}
                                      color="info" size="small"/>
                        }
                        label={<Typography variant="body2">Modelli 1</Typography>}
                        onClick={(e) => e.stopPropagation()}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={1}>
                        <GenericParameterSelect
                            label={t('filtersPanel.model1.parameterSelect.label')}
                            value={model1Stations.visibleParam}
                            onChange={(e) => setVisibleParam('model1Stations', e.target.value)}
                            params={registry["models1"]?.params}
                            fullWidth
                            size="small"
                        />

                        <StationTypeSelect
                            value={model1Stations.filters.stationType}
                            onChange={(e) => updateLayerFilters('model1Stations', {stationType: e.target.value})}
                            size="small"
                            fullWidth
                        />

                        <Model1StatusAutocomplete
                            setModel1Statuses={(val) => updateLayerFilters('model1Stations', {status: val.join(',')})}
                            size="small"
                            fullWidth
                        />

                        <Model1ValidationStatusAutocomplete
                            setModel1ValidationStatuses={(val) => updateLayerFilters('model1Stations', {validationStatus: val.join(',')})}
                            size="small"
                            fullWidth
                        />

                        <UsersAutocomplete
                            selectedUsers={model1Stations.filters.user}
                            setSelectedUsers={(val) => updateLayerFilters('model1Stations', {user: val})}
                            size="small"
                            fullWidth
                        />

                        <StationWritePermissionCheckBox
                            value={model1Stations.filters.stationAclModel1Write}
                            onChange={(val) => updateLayerFilters('model1Stations', {stationAclModel1Write: val})}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion {...accordionStyle} >
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                    <FormControlLabel
                        control={
                            <Checkbox checked={models23Layer.active} onChange={() => toggleLayer("models23")}
                                      color="info" size="small"/>
                        }
                        label={<Typography variant="body2">Modelli 2-3</Typography>}
                        onClick={(e) => e.stopPropagation()}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={1}>
                        <StationTypeSelect
                            value={models23Layer.filters.stationType}
                            onChange={(e) => updateLayerFilters('models23', {stationType: e.target.value})}
                            size="small"
                            fullWidth
                        />

                        <Model1StatusAutocomplete
                            setModel1Statuses={(val) => updateLayerFilters('models23', {status: val.join(',')})}
                            size="small"
                            fullWidth
                        />

                        <UsersAutocomplete
                            selectedUsers={models23Layer.filters.user}
                            setSelectedUsers={(val) => updateLayerFilters('models23', {user: val})}
                            size="small"
                            fullWidth
                        />

                        <StationWritePermissionCheckBox
                            value={models23Layer.filters.stationAclModel23Write}
                            onChange={(val) => updateLayerFilters('models23', {stationAclModel23Write: val})}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion {...accordionStyle} >
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                    <FormControlLabel
                        control={
                            <Checkbox checked={stationsLayer.active} onChange={() => toggleLayer("stations")}
                                      color="info" size="small"/>
                        }
                        label={<Typography variant="body2">Stazioni</Typography>}
                        onClick={(e) => e.stopPropagation()}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={1}>
                        <StationVisibilityAutocomplete
                            setStationVisibility={(val) => updateLayerFilters('stations', {visibility: val})}
                            size="small"
                            fullWidth
                        />

                        <ProvinceAutocomplete
                            value={stationsLayer.filters.province}
                            onChange={(val) => updateLayerFilters('stations', {province: val, municipality: null})}
                            size="small"
                            fullWidth
                            MDInputProps={{
                                label: t('stations:filters.provinceId'),
                                size: "small"
                            }}
                        />

                        <MunicipalityAutocomplete
                            provinceId={stationsLayer.filters.province}
                            value={stationsLayer.filters.municipality}
                            onChange={(val) => updateLayerFilters('stations', {municipality: val})}
                            size="small"
                            fullWidth
                            MDInputProps={{
                                label: t('stations:filters.municipalityId'),
                                size: "small"
                            }}
                        />

                        <StationTypeSelect
                            value={stationsLayer.filters.type}
                            onChange={(e) => updateLayerFilters('stations', {type: e.target.value})}
                            size="small"
                            fullWidth
                        />
                        <CurrentlyActiveSelect
                            value={stationsLayer.filters.currentlyActive}
                            onChange={(e) => updateLayerFilters('stations', {currentlyActive: e.target.value})}
                            size="small"
                            fullWidth
                        />
                        <AcceptsModelSelect
                            modelType="model1"
                            value={stationsLayer.filters.acceptsModel1}
                            onChange={(e) => updateLayerFilters('stations', {acceptsModel1: e.target.value})}
                            size="small"
                            fullWidth
                        />
                        <AcceptsModelSelect
                            modelType="model23"
                            value={stationsLayer.filters.acceptsModel23}
                            onChange={(e) => updateLayerFilters('stations', {acceptsModel23: e.target.value})}
                            size="small"
                            fullWidth
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion {...accordionStyle} >
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                    <FormControlLabel
                        control={
                            <Checkbox checked={microzonesLayer.active}
                                      onChange={() => toggleLayer("microzones")}
                                      color="info" size="small"/>
                        }
                        label={<Typography variant="body2">Microzone</Typography>}
                        onClick={(e) => e.stopPropagation()}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    <GenericParameterSelect
                        label={t('filtersPanel.microzones.parameterSelect.label', 'Parametro')}
                        value={microzonesLayer.visibleParam}
                        onChange={(e) => setVisibleParam('microzones', e.target.value)}
                        params={registry["microzones"]?.params}
                        fullWidth
                        size="small"
                    />
                </AccordionDetails>
            </Accordion>

            <Accordion {...accordionStyle} >
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>} sx={{p: 0}}>
                    <FormControlLabel
                        control={
                            <Checkbox checked={incidentsLayer.active}
                                      onChange={() => toggleLayer("incidents")}
                                      color="info" size="small"/>
                        }
                        label={<Typography variant="body2">Incidenti</Typography>}
                        onClick={(e) => e.stopPropagation()}
                    />
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={1}>
                        <GenericParameterSelect
                            label={t('filtersPanel.incidents.parameterSelect.label', 'Parametro')}
                            value={incidentsLayer.visibleParam}
                            onChange={(e) => setVisibleParam('incidents', e.target.value)}
                            params={registry["incidents"]?.params}
                            fullWidth
                            size="small"
                        />

                        <Divider sx={{my: 1}}/>

                        <SelectFromRegistry
                            value={incidentsLayer.filters.incidentCategory}
                            onChange={(e) => updateLayerFilters('incidents', {
                                incidentCategory: e.target.value,
                                incidentSubcategory: null
                            })}
                            label={t('incidents:filters.incidentCategory')}
                            registry={incidentRegistry?.incidentCategoryV2 ?? []}
                            size="small"
                            fullWidth
                        />

                        <SelectFromRegistry
                            value={incidentsLayer.filters.incidentSubcategory}
                            onChange={(e) => updateLayerFilters('incidents', {incidentSubcategory: e.target.value})}
                            label={t('incidents:filters.incidentSubcategory')}
                            registry={(incidentRegistry?.incidentSubcategory ?? []).filter((subcategory) => (
                                incidentSettings.incidentCategoryGroups?.[incidentsLayer.filters.incidentCategory]?.includes(subcategory.code)
                            ))}
                            disabled={!incidentsLayer.filters.incidentCategory}
                            size="small"
                            fullWidth
                        />

                        <RegionAutocomplete
                            value={incidentsLayer.filters.region}
                            onChange={(val) => updateLayerFilters('incidents', {
                                region: val,
                                province: null,
                                municipality: null
                            })}
                            size="small"
                            fullWidth
                            MDInputProps={{
                                label: t('incidents:filters.regionId'),
                                size: "small"
                            }}
                        />

                        <ProvinceAutocomplete
                            regionId={incidentsLayer.filters.region}
                            value={incidentsLayer.filters.province}
                            onChange={(val) => updateLayerFilters('incidents', {province: val, municipality: null})}
                            size="small"
                            fullWidth
                            MDInputProps={{
                                label: t('incidents:filters.provinceId'),
                                size: "small"
                            }}
                        />

                        <MunicipalityAutocomplete
                            regionId={incidentsLayer.filters.region}
                            provinceId={incidentsLayer.filters.province}
                            value={incidentsLayer.filters.municipality}
                            onChange={(val) => updateLayerFilters('incidents', {municipality: val})}
                            size="small"
                            fullWidth
                            MDInputProps={{
                                label: t('incidents:filters.municipalityId'),
                                size: "small"
                            }}
                        />
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Stack>

    </Stack>
}

export default FiltersPanel;